import React from "react";
import PropTypes from "prop-types";
import { Column, Field, Control, Label, Input, Button, Icon, Box } from "rbx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useHistory, useParams } from "react-router-dom";
import DataTable from "../../../../components/DataTable";
import { INVENTORY_ON_HAND_COLUMNS, VENDORS_COLUMNS } from "../columns";
import { ITEM_TRANSACTION_COLUMNS } from "../../ItemTransactions/columns";
import { ALL_INVENTORY_ON_HAND_QUERY } from "../../../../graphql/Items/InventoryOnHand.graphql";
import HorizontalRule from "../../../../styles/HorizontalRule";
import { ALL_ITEM_TRANSACTIONS_VIEW_QUERY } from "../../../../graphql/Items/ItemTransactions.graphql";
import { ALL_ITEM_VENDORS_QUERY } from "../../../../graphql/Vendors/ItemVendors.graphql";
import { AddItemVendorForm } from "../../../../components/Vendors";
import { useCompanyParameters } from "../../../../utils/hooks";
import {
  BooleanInput,
  CurrencyInput,
  ItemCategorySelect,
  GLMainAccountSelect
} from "../../../../components/inputs";
import { useAuthorization } from "../../../../components/Authorization";

const Details = ({
  editing,
  item,
  onChange,
  addItemVendor,
  setModalOpen,
  codes
}) => {
  const { id } = useParams();
  const history = useHistory();
  const { data: CompanySettings } = useCompanyParameters();
  const isAdmin = useAuthorization(["ADMIN"]);
  const isAdminOrFinance = useAuthorization(["ADMIN", "FIN"]);

  const Auth = useAuthorization(["ADMIN", "FLEET", "FIN"]);
  const codeStates = {
    asset: codes.find(c => c.CodeId === "A"),
    expense: codes.find(c => c.CodeId === "E")
  };

  const handleRowClick = itemTransaction => {
    if (itemTransaction.WorkOrderRecIdRef) {
      return history.push(
        `/fleet/work-orders/${itemTransaction.WorkOrderRecIdRef}/main-information`
      );
    }
    return history.push(
      `/inventory/item-transactions/${
        itemTransaction.RecId
      }/details/${itemTransaction.BatchNo || 0}`
    );
  };
  if (!id) return null;

  return (
    <React.Fragment>
      <Column.Group>
        <Column size={2}>
          <Field>
            <Control>
              <Label>Item #</Label>
              <Input
                disabled
                readOnly
                required
                autocomplete="new"
                name="ItemNo"
                type="text"
                value={item.ItemNo}
                onChange={onChange}
              />
            </Control>
          </Field>
          <Field kind="group">
            <Control expanded>
              <Label>Min Qty</Label>
              <Input
                name="MinQty"
                readOnly={!editing}
                type="number"
                value={item?.MinQty}
                onChange={onChange}
              />
            </Control>
            <Control expanded>
              <Label>Max Qty</Label>
              <Input
                name="MaxQty"
                readOnly={!editing}
                type="number"
                value={item?.MaxQty}
                onChange={onChange}
              />
            </Control>
          </Field>
        </Column>
        <Column>
          <Field>
            <Control>
              <Label>Description</Label>
              <Input
                required
                autocomplete="new"
                name="Description"
                readOnly={!editing}
                value={item?.Description}
                onChange={onChange}
              />
            </Control>
          </Field>
          <Field kind="group">
            {Auth && (
              <Control expanded>
                <Label>Last Purchase Cost</Label>
                <Input
                  disabled
                  readOnly
                  name="LastPurchaseCost"
                  type="text"
                  value={item?.LastPurchaseCost}
                  onChange={onChange}
                />
              </Control>
            )}
            {Auth &&
              CompanySettings?.InventoryCostMethod?.CodeDescription ===
                "Standard Cost" && (
                <Control expanded>
                  <Label>Standard Cost</Label>
                  <Input
                    name="StdCost"
                    readOnly={
                      !editing ||
                      !isAdmin ||
                      !(
                        CompanySettings &&
                        CompanySettings.InventoryCostMethod &&
                        CompanySettings.InventoryCostMethod.CodeDescription ===
                          "Standard Cost"
                      )
                    }
                    type="text"
                    value={item?.StdCost}
                    onChange={onChange}
                  />
                </Control>
              )}
            {Auth &&
              CompanySettings?.InventoryCostMethod?.CodeDescription ===
                "Average Cost" && (
                <Control expanded>
                  <Label>Average Cost</Label>
                  <Input
                    name="AvgCost"
                    readOnly={
                      !editing ||
                      !isAdmin ||
                      !(
                        CompanySettings &&
                        CompanySettings.InventoryCostMethod &&
                        CompanySettings.InventoryCostMethod.CodeDescription ===
                          "Average Cost"
                      )
                    }
                    type="text"
                    value={item?.AvgCost}
                    onChange={onChange}
                  />
                </Control>
              )}
          </Field>
        </Column>
        <Column size={3}>
          <Field kind="group">
            <Control>
              <ItemCategorySelect
                icon="globe"
                label="Item Category"
                name="ItemCatgRecIdRef"
                readOnly={!editing}
                value={item?.ItemCatgRecIdRef}
                onChange={onChange}
              />
            </Control>
            <Control>
              <Label>Active</Label>
              <BooleanInput
                name="Active"
                readOnly={!editing}
                value={item?.Active}
                onChange={onChange}
              />
            </Control>
            {CompanySettings.AllowMaintenanceBilling && (
              <Control>
                <Label>Allow Discounts</Label>
                <BooleanInput
                  name="AllowDiscounts"
                  readOnly={!editing}
                  value={item?.AllowDiscounts}
                  onChange={onChange}
                />
              </Control>
            )}
          </Field>
          <Field kind="group">
            {CompanySettings.AllowMaintenanceBilling && (
              <Control expanded>
                <Label>List Price</Label>
                <CurrencyInput
                  name="ListPrice"
                  readOnly={!editing}
                  value={item?.ListPrice}
                  onChange={onChange}
                />
              </Control>
            )}
          </Field>
        </Column>
        {isAdminOrFinance && (
          <Column>
            <Box>
              <Column.Group>
                <Column>
                  <Field>
                    <Control expanded>
                      <GLMainAccountSelect
                        disabled={!editing}
                        label="Inventory GL Override"
                        name="InvGLRecIdRef"
                        value={item?.InvGLRecIdRef}
                        where={{
                          TypeRecIdRef: codeStates.asset.RecId
                        }}
                        onChange={onChange}
                      />
                    </Control>
                  </Field>
                  <Field>
                    <Control expanded>
                      <GLMainAccountSelect
                        disabled={!editing}
                        label="COGS GL Override "
                        name="CogsGLRecIdRef"
                        value={item?.CogsGLRecIdRef}
                        where={{
                          TypeRecIdRef: codeStates.expense.RecId
                        }}
                        onChange={onChange}
                      />
                    </Control>
                  </Field>
                </Column>
              </Column.Group>
            </Box>
          </Column>
        )}
      </Column.Group>
      <HorizontalRule />
      <div className="item-details-table-container">
        <div>
          <div style={{ display: "flex", alignItems: "flex-end", height: 31 }}>
            <Label style={{ paddingBottom: "0.5rem" }}>
              Quantity On Hand{" "}
              <span style={{ fontWeight: "bold", fontSize: "larger" }}>
                {item?.OH ? `(${item.OH})` : ""}
              </span>
            </Label>
          </div>
          {CompanySettings && (
            <DataTable
              columns={INVENTORY_ON_HAND_COLUMNS()}
              dataQuery={ALL_INVENTORY_ON_HAND_QUERY}
              initialSortBy={[
                { id: "PrimaryStorageDimension", desc: false },
                { id: "SecondaryStorageDimension", desc: false },
                { id: "TertiaryStorageDimension", desc: false }
              ]}
              name="InventoryOnHand"
              where={{ Active: true, ItemRecIdRef: item?.RecId }}
            />
          )}
        </div>
        <div>
          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <Label style={{ flex: 1 }}>Vendors</Label>
            <div>
              <Button
                color="success"
                size="small"
                style={{ marginBottom: "0.5rem" }}
                type="button"
                onClick={addItemVendor}
              >
                <Icon>
                  <FontAwesomeIcon icon="plus" />
                </Icon>
              </Button>
            </div>
          </div>
          <div style={{ maxHeight: 200, overflowY: "auto" }}>
            <DataTable
              columns={VENDORS_COLUMNS}
              dataQuery={ALL_ITEM_VENDORS_QUERY}
              initialSortBy={[
                { id: "IsPrimary", desc: false },
                { id: "VendorName", desc: false }
              ]}
              name="VW_ItemVendor"
              where={{ ItemRecIdRef: parseInt(id, 10) }}
              onRowClick={ItemVendor =>
                setModalOpen(
                  true,
                  <AddItemVendorForm
                    ItemRecIdRef={item.RecId}
                    ItemVendor={ItemVendor}
                    onComplete={() => setModalOpen(false, "")}
                  />
                )
              }
            />
          </div>
        </div>
      </div>
      <HorizontalRule />
      <div>
        <Label>Transactions</Label>
        <DataTable
          columns={ITEM_TRANSACTION_COLUMNS(true, CompanySettings)}
          dataQuery={ALL_ITEM_TRANSACTIONS_VIEW_QUERY}
          initialSortBy={[
            { id: "Posted", desc: false },
            { id: "PostedDate", desc: true }
          ]}
          name="VW_ItemTransaction"
          where={{ ItemRecIdRef: parseInt(id, 10) }}
          onRowClick={handleRowClick}
        />
      </div>
    </React.Fragment>
  );
};

Details.propTypes = {
  editing: PropTypes.bool,
  item: PropTypes.object,
  onChange: PropTypes.func,
  addItemVendor: PropTypes.func,
  setModalOpen: PropTypes.func,
  codes: PropTypes.array
};

export default Details;
